import {
  Avatar,
  Box,
  Container,
  Flex,
  Heading,
  Icon,
  Img,
  Link,
  SimpleGrid,
  Tag,
  TagLabel,
  Text,
} from '@chakra-ui/react';
import React from 'react';
import { GoDotFill } from 'react-icons/go';
import CustomButton from '@/components/common/CustomButton';

const ImgAndDetail: React.FC<{ imgSrc: string; detail: string }> = ({
  detail,
  imgSrc,
}) => (
  <Flex align="center" columnGap="7px">
    <Img src={imgSrc} boxSize="20px" />
    <Text
      fontSize="18px"
      fontWeight="700"
      lineHeight="150%"
      color="text.mediumBlack"
      noOfLines={1}
    >
      {detail}
    </Text>
  </Flex>
);

interface Props {
  imgSrc?: string;
  imgType?: string;
  title: string;
  coverImgLink?: string;
  startDate?: string;
  startTime?: string;
  mainTagText?: string;
  accentLabel?: string;
  contact: {
    name: string;
    designation?: string;
    image?: string;
    email: string;
    phone?: string;
  }[];
  button: {
    text: string;
    link?: string;
    external: boolean;
  };
}

const titleLists = [
  'ESG Imperative and Opportunities for Investors',
  'IVCA Private Credit Summit 2023',
  'IVCA CAT III Summit',
];

const TopCover: React.FC<Props> = ({
  imgSrc,
  imgType,
  title,
  startDate,
  startTime,
  accentLabel,
  button,
  contact,
  mainTagText,
  coverImgLink,
}) => (
  <Box bgColor="secondaryPurple.100" pt="31px" pb={{ xs: '36px', lg: '54px' }}>
    <Container maxW="1245px">
      {imgType === 'banner' ? (
        <Link href={coverImgLink && coverImgLink} target="_blank">
          <Img src={imgSrc} w="full" />
        </Link>
      ) : (
        <Link href={coverImgLink && coverImgLink} target="_blank">
          <Img
            src={imgSrc}
            w={titleLists.includes(title) ? '718px' : 'full'}
            maxH={titleLists.includes(title) ? '800px' : '701px'}
            margin={titleLists.includes(title) ? 'auto' : '0px'}
            objectFit="contain"
            alt="event-image"
          />
        </Link>
      )}

      <Box mt={{ xs: '24px', lg: '33px' }}>
        <Heading
          fontSize={{ xs: '32px', lg: '42px' }}
          fontWeight="800"
          lineHeight="130%"
          color="secondaryPurple.900"
        >
          {title}
        </Heading>
        <Flex
          //   pl="10px"
          mt={{ xs: '18px', md: '12px' }}
          columnGap="31px"
          align={{ xs: 'flex-start', md: 'center' }}
          flexDir={{ xs: 'column', md: 'row' }}
        >
          <Flex columnGap="9px" rowGap="2px" align="center">
            <Flex columnGap="9px" align="center">
              <Img src="/svg/calendar-icon.svg" boxSize="28px" />
              <Text
                fontSize={{ xs: '14px', lg: '16px' }}
                fontWeight="600"
                lineHeight="150%"
                letterSpacing="-0.48px"
                color="secondaryPurple.700"
              >
                {startDate}
              </Text>
            </Flex>
            {startTime === '' ? null : (
              <>
                <Icon
                  as={GoDotFill}
                  fontSize="16px"
                  color="secondaryPurple.700"
                />
                {startTime !== '' && (
                  <Text
                    fontSize={{ xs: '14px', lg: '16px' }}
                    fontWeight="600"
                    lineHeight="150%"
                    letterSpacing="-0.48px"
                    color="secondaryPurple.700"
                  >
                    Time: {startTime}
                  </Text>
                )}
              </>
            )}
          </Flex>
          {mainTagText && (
            <Tag
              mt={{ xs: '12px', lg: '0' }}
              variant="brandSecondary"
              size="md"
            >
              <TagLabel fontSize={{ xs: '14px', lg: '16px' }}>
                {mainTagText}
              </TagLabel>
            </Tag>
          )}
        </Flex>
        {accentLabel && (
          <Text
            mt={{ xs: '12px', md: '16px' }}
            fontSize={{ xs: '16px', lg: '18px' }}
            color="secondaryPurple.600"
            fontWeight="800"
            lineHeight="130%"
          >
            {accentLabel}
          </Text>
        )}
        {contact && contact?.length > 0 && (
          <Box mt={{ xs: '18px', lg: '12px' }}>
            <Text
              color="secondaryPurple.900"
              fontSize={{ xs: '16px', lg: '18px' }}
              fontWeight="800"
              lineHeight="130%"
            >
              Contact Details
            </Text>
            {contact?.length === 1 ? (
              contact?.map((item, index) => (
                <Flex
                  flex={1}
                  alignItems="flex-start"
                  key={`${item?.email}-${index}`}
                  mt="12px"
                  columnGap="88px"
                  rowGap="8px"
                  align={{ xs: 'flex-start', lg: 'center' }}
                  flexDir={{ xs: 'column', lg: 'row' }}
                >
                  {item?.name && (
                    <Flex columnGap="12px" align="center">
                      <Img src="/svg/user-icon.svg" />
                      <Text
                        color="secondaryPurple.900"
                        fontSize={{ xs: '14px', lg: '16px' }}
                        fontWeight="600"
                        lineHeight="130%"
                        textTransform="capitalize"
                      >
                        {item?.name}
                      </Text>
                    </Flex>
                  )}
                  {item?.email && (
                    <Link href={`mailto:${item?.email}`} target="_blank">
                      <Flex columnGap="12px" align="center">
                        <Img src="/svg/mail-icon.svg" />
                        <Text
                          color="secondaryPurple.900"
                          fontSize={{ xs: '14px', lg: '16px' }}
                          fontWeight="600"
                          lineHeight="130%"
                        >
                          {item?.email}
                        </Text>
                      </Flex>
                    </Link>
                  )}
                  {item?.phone && (
                    <Link href={`tel:${item?.phone}`}>
                      <Flex columnGap="12px" align="center">
                        <Img src="/svg/call-icon.svg" />
                        <Text
                          color="secondaryPurple.900"
                          fontSize={{ xs: '14px', lg: '16px' }}
                          fontWeight="600"
                          lineHeight="130%"
                        >
                          {item?.phone}
                        </Text>
                      </Flex>
                    </Link>
                  )}
                </Flex>
              ))
            ) : (
              <Flex mt="1rem" w="100%">
                <Flex
                  // columns={{ xs: 1, md: 2, lg: 3 }}
                  w="100%"
                  // spacingX="24px"
                  // spacingY="24px"
                  flexDir={{ xs: 'column', md: 'row' }}
                  justify="center"
                  // justify="space-between"
                  flexWrap="wrap"
                  rowGap="24px"
                  columnGap="24px"
                >
                  {contact.map((item, index) => (
                    <Flex
                      key={index}
                      boxShadow="0px 1.85185px 3.14815px 0px rgba(49, 49, 49, 0.02), 0px 8.14815px 6.51852px 0px rgba(49, 49, 49, 0.04)"
                      w="100%"
                      maxW="380px"
                      bgColor="white"
                      border="1px solid"
                      borderColor="#ededed"
                      color="text.mediumBlack"
                      py="44px"
                      pl="40px"
                      pr="0px"
                      flexDir="column"
                      rowGap="15px"
                      width="100%"
                    >
                      <Flex columnGap="39px">
                        {item?.image && (
                          <Avatar
                            src={item?.image || 'https://bit.ly/broken-link'}
                            boxSize="34px"
                          />
                        )}

                        <Box>
                          <Heading
                            fontSize="22px"
                            fontWeight="800"
                            lineHeight="150%"
                            color="inherit"
                          >
                            {item.name}
                          </Heading>
                          {item?.designation && (
                            <Text
                              fontSize="15px"
                              fontWeight="600"
                              lineHeight="150%"
                              letterSpacing="-0.3px"
                              color="inherit"
                              h="23px"
                              noOfLines={1}
                            >
                              {item?.designation}
                            </Text>
                          )}
                        </Box>
                      </Flex>

                      <Box
                        h="1.5px"
                        w="100%"
                        maxW="141px"
                        alignSelf={item?.image ? 'center' : ''}
                        bgColor="rgba(95, 85, 61, 0.12)"
                      />
                      <Box
                        // flexDir="column"
                        mt="11px"
                        // align="center"
                        // rowGap="6px"
                        ml={item?.image ? '64px' : '0px'}
                      >
                        {item?.phone && (
                          <Link href={`tel:${item?.phone}`} target="_blank">
                            <ImgAndDetail
                              imgSrc="/svg/call-icon.svg"
                              detail={item?.phone}
                            />
                          </Link>
                        )}
                        {item?.email && (
                          <Link href={`tel:${item?.email}`} target="_blank">
                            <ImgAndDetail
                              imgSrc="/svg/mail-icon.svg"
                              detail={item?.email}
                            />
                          </Link>
                        )}
                      </Box>
                    </Flex>
                  ))}
                </Flex>
              </Flex>
            )}
          </Box>
        )}
        <Box mt={{ xs: '24px', lg: '36px' }}>
          {button?.link && (
            <CustomButton
              text={button.text}
              colorScheme="primary"
              link={button.link}
              externalLink
              icon
              iconProps={{
                size: '24px',
              }}
              btnStyle={{
                px: '40px',
                py: '25px',
                pt: '25px',
                pb: '25px',
                pl: '40px',
                w: { xs: 'full', md: 'auto' },
                fontSize: '18px',
                fontWeight: '700',
                lineHeight: '150%',
              }}
            />
          )}
        </Box>
      </Box>
    </Container>
  </Box>
);

export default TopCover;
